import React, {
  SetStateAction,
  createContext,
  useContext,
  useMemo,
  useState,
} from 'react';
import Api from 'talent_search/lib/api';
import type { Paths } from 'talent_search/shared/types';
import type { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import type { RediscoveryProps } from 'talent_matching/types';
import type { GreenhouseFilterPreset } from 'talent_matching/talent_rediscovery/types';

export interface RediscoveryTabContextValue {
  apiClient: Api;
  paths: Paths;
  milestones: Item[];
  currencies: Item[];
  focusAttributes: Item[];
  savedSearchOptions: Item[];
  setSavedSearchOptions: (options: SetStateAction<Item[]>) => void;
  greenhouseFilterPresets: GreenhouseFilterPreset[];
  unfilteredCount: number;
  similarJobOptions: Item[];
  savedFilterSet: Item | null;
  setSavedFilterSet: (ss: Item | null) => void;
  initialFilterSet: Item | null;
  activeSidePanelTab: string | null;
  setActiveSidePanelTab: (tab: string) => void;
  hiringPlanDepartment: Item | null;
  hiringPlanOffices: Item[];
  defaultPoolName: string | null;
}

const RediscoveryTabContext = createContext<
  RediscoveryTabContextValue | undefined
>(undefined);

export const useRediscoveryTabContext = () => {
  const context = useContext(RediscoveryTabContext);
  if (!context) {
    throw new Error(
      'useRediscoveryTabContext must be used within a RediscoveryTabContextProvider'
    );
  }
  return context;
};

type RediscoveryTabContextProviderProps = {
  rediscoveryProps: RediscoveryProps;
  children: React.ReactNode;
  activeSidePanelTab: string | null;
  setActiveSidePanelTab: (tab: string) => void;
};

type SavedSearchGroupResponse = {
  label: string;
  value: string;
  items: Array<{
    label: string;
    value: string | number;
    shortLabel?: string;
    filterValues?: Record<string, unknown>;
  }>;
};

function isGroupedFormat(
  options: unknown
): options is SavedSearchGroupResponse[] {
  return Array.isArray(options) && options.length > 0 && 'items' in options[0];
}

function parseGroupedOptions(groups: SavedSearchGroupResponse[]): {
  userOptions: Item[];
  greenhousePresets: GreenhouseFilterPreset[];
} {
  const userGroup = groups.find((g) => g.value === 'your_filters');
  const greenhouseGroup = groups.find((g) => g.value === 'greenhouse_filters');

  const userOptions: Item[] = (userGroup?.items ?? []).map((item) => ({
    value: item.value?.toString() ?? '',
    label: item.label,
  }));

  const greenhousePresets: GreenhouseFilterPreset[] = (
    greenhouseGroup?.items ?? []
  ).map((item) => ({
    label: item.label,
    value: item.value?.toString() ?? '',
    shortLabel: item.shortLabel,
    filterValues: item.filterValues ?? {},
  }));

  return { userOptions, greenhousePresets };
}

export const RediscoveryTabContextProvider = ({
  rediscoveryProps,
  children,
  activeSidePanelTab,
  setActiveSidePanelTab,
}: RediscoveryTabContextProviderProps) => {
  const { initialUserOptions, greenhouseFilterPresets } = useMemo(() => {
    const rawOptions = rediscoveryProps.savedSearchOptions;

    if (isGroupedFormat(rawOptions)) {
      const { userOptions, greenhousePresets } =
        parseGroupedOptions(rawOptions);
      return {
        initialUserOptions: userOptions,
        greenhouseFilterPresets: greenhousePresets,
      };
    }

    // Flat format fallback (backward compat)
    const flatOptions: Item[] = (
      (rawOptions as unknown as Array<{ label: string; value: number }>) ?? []
    ).map((item) => ({
      value: item.value?.toString() ?? '',
      label: item.label,
    }));

    return { initialUserOptions: flatOptions, greenhouseFilterPresets: [] };
  }, [rediscoveryProps.savedSearchOptions]);

  const initialFilterSet = useMemo((): Item | null => {
    const tierValue = rediscoveryProps.defaultFilterTier;
    if (!tierValue) {
      return null;
    }
    const preset = greenhouseFilterPresets.find((p) => p.value === tierValue);
    if (!preset) {
      return null;
    }
    return { label: preset.shortLabel ?? preset.label, value: preset.value };
  }, [rediscoveryProps.defaultFilterTier, greenhouseFilterPresets]);

  const [savedFilterSet, setSavedFilterSet] = useState<Item | null>(
    initialFilterSet
  );

  const [savedSearchOptions, setSavedSearchOptions] =
    useState<Item[]>(initialUserOptions);

  const apiClient = useMemo(() => {
    return new Api(rediscoveryProps.paths);
  }, [rediscoveryProps?.paths]);

  const contextValue: RediscoveryTabContextValue = useMemo(
    () => ({
      apiClient,
      paths: rediscoveryProps.paths,
      milestones: rediscoveryProps.milestones,
      currencies: rediscoveryProps.currencies,
      focusAttributes: rediscoveryProps.focusAttributes,
      savedSearchOptions,
      setSavedSearchOptions,
      greenhouseFilterPresets,
      unfilteredCount: rediscoveryProps.unfilteredCount,
      similarJobOptions: rediscoveryProps.similarJobOptions ?? [],
      savedFilterSet,
      setSavedFilterSet,
      initialFilterSet,
      activeSidePanelTab,
      setActiveSidePanelTab,
      hiringPlanDepartment: rediscoveryProps.hiringPlanDepartment ?? null,
      hiringPlanOffices: rediscoveryProps.hiringPlanOffices ?? [],
      defaultPoolName: rediscoveryProps.defaultPoolName ?? null,
    }),
    [
      rediscoveryProps,
      apiClient,
      savedSearchOptions,
      greenhouseFilterPresets,
      savedFilterSet,
      initialFilterSet,
      activeSidePanelTab,
      setActiveSidePanelTab,
    ]
  );

  return (
    <RediscoveryTabContext.Provider value={contextValue}>
      {children}
    </RediscoveryTabContext.Provider>
  );
};
